import React from 'react';
import DOMPurify from 'dompurify';

// Configure DOMPurify for extra security
const sanitizeConfig = {
  ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'ul', 'ol', 'li', 'blockquote', 'code', 'pre', 'img', 'a', 'del', 'hr', 'table', 'tr', 'td', 'div', 'span'],
  ALLOWED_ATTR: ['src', 'alt', 'href', 'target', 'rel', 'class', 'loading', 'style'],
  FORBID_TAGS: ['script', 'object', 'embed', 'base', 'link', 'meta'],
  FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover']
};

interface MarkdownRendererProps {
  content: string;
  className?: string;
  textOnly?: boolean; // Pour les bandeaux/previews - supprime vidéos et éléments complexes
  isPrestige?: boolean; // Pour les serveurs prestige - active l'effet de scroll
}

export const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({ 
  content, 
  className = '', 
  textOnly = false,
  isPrestige = false 
}) => {
  const stripComplexElements = (text: string) => {
    if (!textOnly) return text;
    
    // Supprimer les éléments vidéo et multimédia pour les bandeaux
    return text
      // Supprimer les balises video
      .replace(/<video[^>]*>.*?<\/video>/gis, '')
      // Supprimer les balises iframe (YouTube, etc.)
      .replace(/<iframe[^>]*>.*?<\/iframe>/gis, '')
      // Supprimer les balises embed
      .replace(/<embed[^>]*\/?>/gis, '')
      // Supprimer les balises object
      .replace(/<object[^>]*>.*?<\/object>/gis, '')
      // Supprimer les balises audio
      .replace(/<audio[^>]*>.*?<\/audio>/gis, '')
      // Supprimer les scripts
      .replace(/<script[^>]*>.*?<\/script>/gis, '')
      // Supprimer les styles inline
      .replace(/<style[^>]*>.*?<\/style>/gis, '')
      // Supprimer les liens vers des vidéos YouTube/Vimeo et les remplacer par du texte
      .replace(/https?:\/\/(www\.)?(youtube\.com\/watch\?v=|youtu\.be\/|vimeo\.com\/)[^\s]+/gi, '[Vidéo]')
      // Supprimer les images si c'est pour un preview textuel
      .replace(/<img[^>]*>/gi, textOnly ? '' : '$&')
      // Nettoyer espaces multiples (conserver les retours à la ligne)
      .replace(/[^\S\r\n]+/g, ' ')
      .trim();
  };

  const renderMarkdown = (text: string) => {
    // Filtrer le contenu si c'est pour un bandeau
    const cleanedText = stripComplexElements(text);
    
    if (textOnly) {
      // Mode textOnly - traitement simple, conserver les retours à la ligne
      let result = cleanedText
        .replace(/\[!\[[^\]]*\]\([^)]+\)\]\([^)]+\)/g, '')
        .replace(/!\[[^\]]*\]\([^)]+\)/g, '')
        .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
        .replace(/\*\*(.*?)\*\*/g, '$1')
        .replace(/__(.*?)__/g, '$1')
        .replace(/\*([^*]+)\*/g, '$1')
        .replace(/_([^_]+)_/g, '$1')
        .replace(/`([^`]+)`/g, '$1')
        .replace(/```([\s\S]*?)```/g, '$1')
        .replace(/~~(.*?)~~/g, '$1')
        .replace(/^#{1,6}\s(.+)$/gm, '$1\n')
        .replace(/^>\s(.+)$/gm, '$1\n')
        .replace(/^\s*[-*]\s(.+)$/gm, '$1\n')
        .replace(/^\s*\d+\.\s(.+)$/gm, '$1\n')
        .replace(/[^\S\n]+/g, ' ')
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'")
        .trim();
      if (!isPrestige) {
        result = result.substring(0, 200) + (result.length > 200 ? '...' : '');
      }
      return result;
    }
    
    let html = cleanedText;
    
    // 1. Code blocks FIRST (pour éviter qu'ils soient modifiés par d'autres regex)
    html = html.replace(/```([\s\S]*?)```/g, '<pre class="bg-slate-800 border border-slate-600 rounded-lg p-4 overflow-x-auto my-4"><code class="text-gaming-primary font-mono text-sm">$1</code></pre>');
    
    // 2. Images avec liens [![alt](img)](link)
    html = html.replace(/\[!\[([^\]]*)\]\(([^)]+)\)\]\(([^)]+)\)/g, '<a href="$3" target="_blank" rel="noopener noreferrer"><img src="$2" alt="$1" style="max-width: 80%; height: auto; display: inline-block; margin: 8px 0;" loading="lazy" /></a>');
    
    // 3. Images simples ![alt](url)
    html = html.replace(/!\[([^\]]*)\]\(([^)]+)\)/g, '<img src="$2" alt="$1" style="max-width: 80%; height: auto; display: inline-block; margin: 8px 0;" loading="lazy" />');
    
    // 4. Links [text](url)
    html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" class="text-gaming-primary hover:text-gaming-secondary underline transition-colors">$1</a>');
    
    // 5. Headers (du plus spécifique au plus général)
    html = html.replace(/^#### (.*$)/gim, '<h4 class="text-base font-bold text-white mb-1 mt-3">$1</h4>');
    html = html.replace(/^### (.*$)/gim, '<h3 class="text-lg font-bold text-white mb-1 mt-3">$1</h3>');
    html = html.replace(/^## (.*$)/gim, '<h2 class="text-xl font-bold text-white mb-2 mt-3">$1</h2>');
    html = html.replace(/^# (.*$)/gim, '<h1 class="text-2xl font-bold text-white mb-2 mt-3">$1</h1>');
    
    // 6. Bold (avant italic pour éviter les conflits)
    html = html.replace(/\*\*([^\*]+)\*\*/g, '<strong class="font-bold text-white">$1</strong>');
    html = html.replace(/__([^_]+)__/g, '<strong class="font-bold text-white">$1</strong>');
    
    // 7. Italic (attention aux * qui ne sont pas des puces de liste)
    html = html.replace(/\*([^\*\n]+)\*/g, '<em class="italic text-slate-300">$1</em>');
    html = html.replace(/_([^_\n]+)_/g, '<em class="italic text-slate-300">$1</em>');
    
    // 8. Strikethrough
    html = html.replace(/~~([^~]+)~~/g, '<del class="line-through text-slate-500">$1</del>');
    
    // 9. Code inline
    html = html.replace(/`([^`]+)`/g, '<code class="bg-slate-800 text-gaming-primary px-1 rounded text-sm font-mono">$1</code>');
    
    // 10. Blockquotes
    html = html.replace(/^&gt; (.+)$/gm, '<blockquote class="border-l-4 border-gaming-primary pl-4 py-2 bg-slate-800/30 rounded-r italic text-slate-300 my-2">$1</blockquote>');
    html = html.replace(/^> (.+)$/gm, '<blockquote class="border-l-4 border-gaming-primary pl-4 py-2 bg-slate-800/30 rounded-r italic text-slate-300 my-2">$1</blockquote>');
    
    // 11. Horizontal rules
    html = html.replace(/^---$/gm, '<hr class="border-slate-600 my-3" />');
    html = html.replace(/^\*\*\*$/gm, '<hr class="border-slate-600 my-3" />');
    
    // 12. Listes non ordonnées (- ou *)
    html = html.replace(/^[\s]*[-*]\s+(.+)$/gm, '||LI_START||$1||LI_END||');
    
    // 13. Listes ordonnées (1., 2., etc.)
    html = html.replace(/^[\s]*\d+\.\s+(.+)$/gm, '||OLI_START||$1||OLI_END||');
    
    // 14. Wrapper les listes groupées
    html = html.replace(/((?:\|\|LI_START\|\|[^\|]+\|\|LI_END\|\|\n?)+)/g, (match) => {
      const items = match.match(/\|\|LI_START\|\|(.*?)\|\|LI_END\|\|/g)?.map(item => 
        item.replace(/\|\|LI_START\|\|/, '').replace(/\|\|LI_END\|\|/, '')
      ) || [];
      return '<ul class="list-disc ml-6 mb-2 space-y-0.5">' + items.map(item => `<li class="text-slate-300">${item.trim()}</li>`).join('') + '</ul>';
    });
    
    html = html.replace(/((?:\|\|OLI_START\|\|[^\|]+\|\|OLI_END\|\|\n?)+)/g, (match) => {
      const items = match.match(/\|\|OLI_START\|\|(.*?)\|\|OLI_END\|\|/g)?.map(item => 
        item.replace(/\|\|OLI_START\|\|/, '').replace(/\|\|OLI_END\|\|/, '')
      ) || [];
      return '<ol class="list-decimal ml-6 mb-2 space-y-0.5">' + items.map(item => `<li class="text-slate-300">${item.trim()}</li>`).join('') + '</ol>';
    });
    
    // 15. Gérer les lignes vides (double \n devient un espacement)
    html = html.replace(/\n\n+/g, '<div class="h-3"></div>');
    
    // 16. Line breaks simples (un seul \n)
    html = html.replace(/\n/g, '<br />');
    
    // 17. Nettoyer les <br> inutiles après certaines balises
    html = html.replace(/(<\/h[1-6]>)<br \/>/g, '$1');
    html = html.replace(/(<\/ul>)<br \/>/g, '$1');
    html = html.replace(/(<\/ol>)<br \/>/g, '$1');
    html = html.replace(/(<hr[^>]*\/>)<br \/>/g, '$1');
    html = html.replace(/(<\/blockquote>)<br \/>/g, '$1');
    html = html.replace(/(<\/pre>)<br \/>/g, '$1');
    
    return html;
  };

  // En mode textOnly, retourner du texte simple
  if (textOnly) {
    return (
      <span className={`${className} ${isPrestige ? 'prestige-description-scroll' : ''}`}>
        {renderMarkdown(content)}
      </span>
    );
  }

  return (
    <div 
      className={`prose prose-invert max-w-none ${className}`}
      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(renderMarkdown(content), sanitizeConfig) }}
    />
  );
};